import type { CookiePreferences } from "@/types/cookieConsent";

export const COOKIE_CONSENT_KEY = 'shoresight_cookie_consent';
export const COOKIE_CONSENT_VERSION = 1;
export const CONSENT_EXPIRY_DAYS = 365;

// Check if browser supports Global Privacy Control
export function hasGlobalPrivacyControl(): boolean {
  if (typeof window === 'undefined') return false;
  return (navigator as any).globalPrivacyControl === true || navigator.doNotTrack === '1';
}

// Get current preferences from localStorage
export function getCookiePreferences(): CookiePreferences | null {
  if (typeof window === 'undefined') return null;
  
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) return null;
    
    const preferences: CookiePreferences = JSON.parse(stored);
    
    // Check if preferences are expired (> 365 days)
    const age = Date.now() - preferences.timestamp;
    const maxAge = CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    
    if (age > maxAge) {
      clearCookiePreferences();
      return null;
    }
    
    return preferences;
  } catch (error) {
    console.error('Error reading cookie preferences:', error);
    return null;
  }
}

// Save preferences to localStorage
export function saveCookiePreferences(preferences: CookiePreferences): void {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(preferences));
  } catch (error) {
    console.error('Error saving cookie preferences:', error);
  }
}

// Check if user has made a consent decision
export function hasConsentDecision(): boolean {
  return getCookiePreferences() !== null;
}

// Check if specific category is enabled
export function isCategoryEnabled(category: 'analytics' | 'marketing'): boolean {
  const preferences = getCookiePreferences();
  if (!preferences) return false; // Default to false if no decision made
  return preferences[category];
}

// Clear all preferences
export function clearCookiePreferences(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  
  // Clear tracking cookies
  deleteCookie('_ga');
  deleteCookie('_gid');
  deleteCookie('_gat');
  deleteCookie('_fbp');
  deleteCookie('_fbc');
  deleteCookie('_gcl_au');
  deleteCookie('_gcl_aw');
}

// Handle "Do Not Sell" request
export function doNotSell(): void {
  const preferences: CookiePreferences = {
    necessary: true,
    analytics: false,
    marketing: false,
    timestamp: Date.now(),
    version: COOKIE_CONSENT_VERSION,
    gpcHonored: false,
    method: 'do_not_sell'
  };
  
  saveCookiePreferences(preferences);
  disableAllTracking();
}

// Initialize with GPC if enabled
export function initializeWithGPC(): CookiePreferences {
  const hasGPC = hasGlobalPrivacyControl();
  
  const preferences: CookiePreferences = {
    necessary: true,
    analytics: !hasGPC,
    marketing: !hasGPC,
    timestamp: Date.now(),
    version: COOKIE_CONSENT_VERSION,
    gpcHonored: hasGPC,
    method: hasGPC ? 'gpc_auto' : 'banner_accept'
  };
  
  return preferences;
}

// Cookie utility functions
export function setCookie(name: string, value: string, days: number): void {
  if (typeof document === 'undefined') return;
  
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  
  return null;
}

export function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
}

// Tracking script management
export function enableGoogleAnalytics(measurementId: string): void {
  if (typeof window === 'undefined' || !measurementId) return;
  
  // Check if already loaded
  if (document.getElementById('ga-script')) return;
  
  const script = document.createElement('script');
  script.id = 'ga-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  
  script.onload = () => {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function(...args: any[]) { window.dataLayer.push(args); };
    window.gtag('js', new Date().toISOString());
    window.gtag('config', measurementId, {
      'anonymize_ip': true,
      'allow_google_signals': false,
    });
  };
  
  document.head.appendChild(script);
}

export function disableGoogleAnalytics(): void {
  if (typeof window === 'undefined') return;
  
  const script = document.getElementById('ga-script');
  if (script) script.remove();
  
  deleteCookie('_ga');
  deleteCookie('_gid');
  deleteCookie('_gat');
  
  if (window.dataLayer) {
    window.dataLayer = [];
  }
  if (window.gtag) {
    delete window.gtag;
  }
}

export function enableMetaPixel(pixelId: string): void {
  if (typeof window === 'undefined' || !pixelId) return;
  
  if (document.getElementById('fb-pixel')) return;
  
  const script = document.createElement('script');
  script.id = 'fb-pixel';
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
  `;
  
  document.head.appendChild(script);
}

export function disableMetaPixel(): void {
  if (typeof window === 'undefined') return;
  
  const script = document.getElementById('fb-pixel');
  if (script) script.remove();
  
  deleteCookie('_fbp');
  deleteCookie('_fbc');
  
  if ((window as any).fbq) {
    delete (window as any).fbq;
    delete (window as any)._fbq;
  }
}

export function disableAllTracking(): void {
  disableGoogleAnalytics();
  disableMetaPixel();
}
